import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { LngLat } from '@alltrails/maps/types/Geo';

export type MeasureMode = 'distance' | 'area' | 'rings';

export type AdvancedBuilderMeasureState = {
  areaPoints: LngLat[];
  distancePoints: LngLat[];
  isActive: boolean;
  isDragging: boolean;
  mode: MeasureMode;
  ringRadii: number[];
  ringsCenter?: LngLat;
  showAreaInAcres: boolean;
};

export const initialState: AdvancedBuilderMeasureState = {
  areaPoints: [],
  distancePoints: [],
  isActive: false,
  isDragging: false,
  mode: 'distance',
  ringRadii: [],
  showAreaInAcres: false
};

const getPathPoints = (state: AdvancedBuilderMeasureState) => (state.mode === 'distance' ? state.distancePoints : state.areaPoints);

const clearRings = (state: AdvancedBuilderMeasureState) => {
  state.ringsCenter = undefined;
  state.ringRadii = [];
};

const canUseRingRadius = (state: AdvancedBuilderMeasureState, radius: number) =>
  Number.isFinite(radius) && radius > 0 && !state.ringRadii.includes(radius);

const isSamePoint = (a: LngLat | undefined, b: LngLat | undefined) => !!a && !!b && a[0] === b[0] && a[1] === b[1];

export const isRingClosed = (points: LngLat[]) => points.length >= 4 && isSamePoint(points[0], points[points.length - 1]);

export const advancedBuilderMeasureSlice = createSlice({
  name: 'advancedBuilderMeasure',
  initialState,
  reducers: {
    setMeasureActive: (state, action: PayloadAction<boolean>) => {
      state.isActive = action.payload;
      state.isDragging = false;
    },
    setMeasureMode: (state, action: PayloadAction<MeasureMode>) => {
      state.mode = action.payload;
    },
    addMeasurePoint: (state, action: PayloadAction<LngLat>) => {
      if (state.mode === 'rings') {
        state.ringsCenter ??= action.payload;
      } else if (state.mode === 'distance' || !isRingClosed(state.areaPoints)) {
        const points = getPathPoints(state);
        if (!isSamePoint(points[points.length - 1], action.payload)) {
          points.push(action.payload);
        }
      }
    },
    moveMeasurePoint: (state, action: PayloadAction<{ index: number; point: LngLat }>) => {
      const { index, point } = action.payload;
      if (state.mode === 'rings') {
        state.ringsCenter = point;
        return;
      }
      const points = getPathPoints(state);
      if (index < 0 || index >= points.length) {
        return;
      }
      const isClosed = state.mode === 'area' && isRingClosed(points);
      points[index] = point;
      if (isClosed && (index === 0 || index === points.length - 1)) {
        points[0] = point;
        points[points.length - 1] = [...point];
      }
    },
    removeMeasurePoint: (state, action: PayloadAction<number>) => {
      if (state.mode === 'rings') {
        clearRings(state);
        return;
      }
      const index = action.payload;
      const points = getPathPoints(state);
      if (index < 0 || index >= points.length) {
        return;
      }
      const wasClosed = state.mode === 'area' && isRingClosed(points);
      points.splice(index, 1);
      if (wasClosed && index < points.length) {
        if (index === 0) {
          points[points.length - 1] = [...points[0]];
        }
        if (points.length < 4) {
          points.pop();
        }
      }
    },
    undoMeasurePoint: state => {
      if (state.mode === 'rings') {
        if (state.ringRadii.pop() === undefined) {
          state.ringsCenter = undefined;
        }
      } else {
        getPathPoints(state).pop();
      }
    },
    clearMeasurement: state => {
      if (state.mode === 'rings') {
        clearRings(state);
        return;
      }
      getPathPoints(state).splice(0);
    },
    closeMeasureArea: state => {
      if (state.areaPoints.length >= 3 && !isRingClosed(state.areaPoints)) {
        state.areaPoints.push([...state.areaPoints[0]]);
      }
    },
    setMeasureDragging: (state, action: PayloadAction<boolean>) => {
      state.isDragging = action.payload;
    },
    addMeasureRing: (state, action: PayloadAction<number>) => {
      if (state.ringsCenter && canUseRingRadius(state, action.payload)) {
        state.ringRadii.push(action.payload);
      }
    },
    moveMeasureRing: (state, action: PayloadAction<{ index: number; radius: number }>) => {
      const { index, radius } = action.payload;
      if (index >= 0 && index < state.ringRadii.length && canUseRingRadius(state, radius)) {
        state.ringRadii[index] = radius;
      }
    },
    removeMeasureRing: (state, action: PayloadAction<number>) => {
      if (action.payload >= 0) {
        state.ringRadii.splice(action.payload, 1);
      }
    },
    setShowAreaInAcres: (state, action: PayloadAction<boolean>) => {
      state.showAreaInAcres = action.payload;
    }
  }
});

export const {
  addMeasurePoint,
  addMeasureRing,
  clearMeasurement,
  closeMeasureArea,
  moveMeasurePoint,
  moveMeasureRing,
  removeMeasurePoint,
  removeMeasureRing,
  setMeasureActive,
  setMeasureDragging,
  setMeasureMode,
  setShowAreaInAcres,
  undoMeasurePoint
} = advancedBuilderMeasureSlice.actions;

export default advancedBuilderMeasureSlice.reducer;
